import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import Collection from 'assets/components/data/Collection';
import ThemeCollectionTable from 'assets/components/data/themes/Theme.Collection.Table';
import DataMap from 'assets/components/dataMap/DataMap';
import { useModalForm } from 'assets/components/form/modalForm/ModalForm';
import Icon from 'assets/components/icon/Icon';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import LineItem from 'models/enums/LineItem';
import { useState } from 'react';
import ClaimTypeJobItemTemplatesEditForm from './ClaimType.Edit.JobItemTemplatesForm';
import useApi from 'assets/hooks/api/useApi';
import LossCode from 'models/claims/lossCode/Model.LossCode';
import { intersectionBy } from 'lodash';

export default function ClaimTypeEditJobItemTemplates() {
  const { lang } = getLocales();
  const { form } = useModalForm<Api.Core.ClaimType.Save.IRequest>();
  const [currentJobItemTemplate, setCurrentJobItemTemplate] = useState<Partial<Model.IClaimTypeJobItemTemplate>>(null);

  const updateJobItemTemplate = (data: Utils.FormData<Model.IClaimTypeJobItemTemplate>, index: number) => {
    const updatedJobItemTemplates = form.data?.claimTypeJobItemTemplates?.map((item, idx) =>
      idx === index ? { ...item, ...data } : item
    );
    form.update({ claimTypeJobItemTemplates: updatedJobItemTemplates });
  };

  const deleteJobItemTemplate = (index: number) => {
    const updatedJobItemTemplates = form.data?.claimTypeJobItemTemplates?.filter((_, idx) => idx !== index);
    form.update({ claimTypeJobItemTemplates: updatedJobItemTemplates });
  };

  const { payload: lossCodePayload } = useApi({
    action: LossCode.list,
  });

  const itemTemplateLossCodes = intersectionBy(lossCodePayload?.data, form.data.lossCodes, 'id');

  return (
    <Collection class={ThemeCollectionTable}>
      <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          class={ThemeButtonCircle}
          render={lang.addNew}
          active
          data={{
            id: -1 * (form.data.claimTypeJobItemTemplates?.length || 0),
            jobItemTypeCode: undefined,
            lossCodes: [],
            adjudication: 0.0,
          }}
          onClick={setCurrentJobItemTemplate}
        />
        {currentJobItemTemplate && (
          <ClaimTypeJobItemTemplatesEditForm
            data={currentJobItemTemplate}
            onCancel={() => setCurrentJobItemTemplate(null)}
            onSave={(newJobItemTemplate) => {
              const filteredJobItemTemplates = form.data?.claimTypeJobItemTemplates?.filter(
                (it) => it?.id !== newJobItemTemplate?.id
              );
              form.update({ claimTypeJobItemTemplates: [...filteredJobItemTemplates, newJobItemTemplate] });
              setCurrentJobItemTemplate(null);
            }}
            lossCodes={itemTemplateLossCodes}
          />
        )}
      </Box>
      <DataMap
        data={form.data?.claimTypeJobItemTemplates}
        render={({ data: jobItem, index }) => (
          <Box
            key={`${jobItem?.id}_${index}`}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            render={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-evenly',
                  width: '100%',
                  gap: Theme.Size.XL,
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: Theme.Size.L, width: '100%' }}>
                  <div style={{ display: 'flex' }}>
                    <SelectInput
                      name="jobItemTypeCode"
                      label={lang.type}
                      data={Object.values(LineItem)
                        .filter((it) => it !== 'Deductible')
                        .map((it) => ({ id: it, title: it }))}
                      value={jobItem?.jobItemTypeCode}
                      onChange={(value) => updateJobItemTemplate(value, index)}
                    />
                    &nbsp;
                    <NumberInput
                      label={lang.adjustedAmt}
                      name="adjudication"
                      value={jobItem?.adjudication}
                      onChange={(value) => updateJobItemTemplate(value, index)}
                      style={{ input: { textAlign: 'right' } }}
                      icon={<Icon>{'CAD'}</Icon>}
                    />
                  </div>
                  <div>
                    <SelectInput
                      name="lossCodes"
                      label={lang.lossCodes}
                      data={itemTemplateLossCodes?.map((it) => it.displayInfo)}
                      value={jobItem.lossCodes?.map((it) => it.id)}
                      onChange={({ lossCodes: idList }) => {
                        const newValue = idList?.map((id) => ({ id }));
                        updateJobItemTemplate({ lossCodes: newValue }, index);
                      }}
                      isMultiselect
                    />
                  </div>
                </div>
                <Button
                  class={ThemeButtonCircle}
                  media={<Icon class="fas-trash" />}
                  data={index}
                  onClick={deleteJobItemTemplate}
                />
              </div>
            }
          />
        )}
      />
    </Collection>
  );
}
